/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import BorderLoader from "components/atoms/loader/BorderLoader";
import { MEDIA_QUERY_FOR_PC } from "constants/mediaQueryForPc";
import { useI18n } from "hooks/i18n/i18n";
import { modalActions } from "modules/modal";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { UndoToast } from "ui/UndoToast";
import { Button } from "ui/chakra/components/Button";

import { dbFirestore } from "../../firestore";
import { useSelector } from "../../store";
import { useBlockedUsers } from "./useBlockedUsers";

import type { BlockedUser, WithId } from "../../firestore/types";

type Props = Record<string, never>;

// i18n の言語コードは Intl のロケール識別子と綴りが違う（zh_cn 等）ため対応表を持つ。
const DATE_LOCALES: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  ja: "ja-JP",
  ko: "ko-KR",
  zh_cn: "zh-CN",
  zh_tw: "zh-TW",
};

const formatBlockedDate = (date: Date, language: string) =>
  new Intl.DateTimeFormat(DATE_LOCALES[language] ?? "ja-JP", {
    dateStyle: "long",
  }).format(date);

export const AccountBlockedUsers: React.FC<Props> = () => {
  const { t, i18n } = useI18n();
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user.uid);
  const blockedUsersState = useBlockedUsers({ userId });
  // error も loading と同じく一覧を出せない状態として扱う。購読が失敗しただけで
  // 「ブロックは 0 件」と表示すると、解除していないものを解除済みに見せてしまう。
  const blockedUsers =
    blockedUsersState.status === "loaded"
      ? blockedUsersState.blockedUsers
      : undefined;
  // 解除中の行だけボタンを止めたいので、ユーザーごとに持つ。
  const [unblockingUserIds, setUnblockingUserIds] = useState<
    Record<string, boolean>
  >({});
  const [unblockedUser, setUnblockedUser] = useState<
    WithId<BlockedUser> | undefined
  >(undefined);
  // トーストは消えた後もアンマウントしないので、解除のたびに key を変えて作り直す。
  // 同じ相手を続けて解除しても出し直せる。
  const [undoUnblockToastVersion, setUndoUnblockToastVersion] = useState(0);
  const classes = blockedUsersStyles();

  // 新しくブロックした順。createdAt を持たない doc をクエリの orderBy が結果から
  // 丸ごと落とすのを避けるため、並べ替えはここで行う。
  const sortedBlockedUsers = useMemo(
    () =>
      blockedUsers &&
      [...blockedUsers].sort(
        (a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()
      ),
    [blockedUsers]
  );

  const handleClickUnblock: (
    user: WithId<BlockedUser>
  ) => React.MouseEventHandler<HTMLButtonElement> = (user) => async () => {
    if (unblockingUserIds[user.id]) {
      return;
    }
    setUnblockingUserIds((ids) => ({ ...ids, [user.id]: true }));

    try {
      // 解除後の一覧更新は useBlockedUsers の onSnapshot が担うので、ここで再取得はしない。
      await dbFirestore.blockedUsers(userId).doc(user.id).delete();
      setUnblockedUser(user);
      setUndoUnblockToastVersion((version) => version + 1);
    } catch (err) {
      console.error(err);
      dispatch(
        modalActions.toggleError({
          msg: t("accountBlock.unblockError"),
        })
      );
    } finally {
      setUnblockingUserIds((ids) => ({ ...ids, [user.id]: false }));
    }
  };

  const handleClickUndoUnblock: () => Promise<void> = async () => {
    const target = unblockedUser;
    if (target === undefined) return;

    try {
      // ブロックし直しではなく取り消しなので、createdAt を今にせず元の値のまま戻す。
      // 一覧の並び順もブロックした時点のままになる。
      await dbFirestore.blockedUsers(userId).doc(target.id).set({
        createdAt: target.createdAt,
        name: target.name,
        eventId: target.eventId,
      });
    } catch (err) {
      console.error(err);
      dispatch(
        modalActions.toggleError({
          msg: t("accountBlock.undoUnblockError"),
        })
      );
    }
  };

  return (
    <div css={classes.page}>
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.account"), link: "/account" },
          { label: t("accountBlock.title"), link: null },
        ]}
      />
      {!sortedBlockedUsers ? (
        <div css={classes.loaderWrapper}>
          <BorderLoader />
        </div>
      ) : sortedBlockedUsers.length > 0 ? (
        <ul css={classes.userList}>
          {sortedBlockedUsers.map((user) => (
            <li key={user.id} css={classes.userItem}>
              <div css={classes.userItemInner}>
                <div css={classes.userInfo}>
                  {/* name はブロック時の投稿者名。未設定なら空文字で保存されている */}
                  <p css={classes.userName}>
                    {user.name || t("accountBlock.noName")}
                  </p>
                  <p css={classes.blockedAt}>
                    {t("accountBlock.blockedAt", {
                      date: formatBlockedDate(
                        user.createdAt.toDate(),
                        i18n.language
                      ),
                    })}
                  </p>
                </div>
                <div css={classes.unblockButtonArea}>
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={unblockingUserIds[user.id] ?? false}
                    onClick={handleClickUnblock(user)}
                  >
                    {t("accountBlock.unblock")}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div css={classes.notApplicable}>
          <p>{t("accountBlock.empty")}</p>
        </div>
      )}
      {unblockedUser && (
        <UndoToast
          key={undoUnblockToastVersion}
          message={t("accountBlock.unblocked", {
            name: unblockedUser.name || t("accountBlock.noName"),
          })}
          actionLabel={t("accountBlock.undo")}
          onClickAction={handleClickUndoUnblock}
          position="fixed"
        />
      )}
    </div>
  );
};

const blockedUsersStyles = () => {
  return {
    page: css({
      width: "100%",
      maxWidth: "1800px",
      // 切り替えは他のスタイルと同じ 768px に合わせる。MUI Box の配列記法だと
      // theme 既定の 600px で切り替わり、この幅では余白だけ PC 用になっていた。
      minHeight: "calc(100vh - 436px)",
      [MEDIA_QUERY_FOR_PC]: {
        minHeight: "calc(100vh - 280px)",
      },
      paddingBottom: "80px",
      margin: "0 auto",
      backgroundColor: "#fff",
    }),
    loaderWrapper: css({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "200px",
    }),
    userList: css({
      display: "flex",
      flexDirection: "column",
      marginTop: "30px",
      // 行の境界は罫線ではなく 1px の隙間と影で表す。
      rowGap: "1px",
    }),
    userItem: css({
      display: "flex",
      justifyContent: "center",
      padding: "8px 0",
      backgroundColor: "#fff",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    }),
    userItemInner: css({
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      width: "100%",
      maxWidth: "600px",
      padding: "0 16px",
    }),
    userInfo: css({
      flex: "1 0 0",
      minWidth: 0,
      maxWidth: "368px",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      rowGap: "4px",
    }),
    userName: css({
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 700,
      color: "#000",
      wordBreak: "break-all",
    }),
    blockedAt: css({
      fontSize: "14px",
      lineHeight: "20px",
      fontWeight: 500,
      color: "rgba(0, 0, 0, 0.48)",
    }),
    unblockButtonArea: css({
      flex: "1 0 0",
      minWidth: 0,
      maxWidth: "232px",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "center",
    }),
    notApplicable: css({
      textAlign: "center",
      padding: "60px 20px",
      p: {
        fontSize: "16px",
        color: "#666",
      },
    }),
  };
};
